import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import * as Styled from './Notification.styled';

const TRANSITION_DURATION = 300;
const EXIT_TRANSITION_DURATION = 200;

const NotificationContainer = ({ isOpen, ...props }) => {
  const containerRef = useRef(document.createElement('div'));

  // Setup / teardown
  useEffect(() => {
    const element = containerRef.current;

    document.body.appendChild(element);
    return () => {
      document.body.removeChild(element);
    };
  }, []);

  return createPortal(
    <CSSTransition
      appear
      in={isOpen}
      classNames="fade"
      timeout={TRANSITION_DURATION}
      unmountOnExit
    >
      <Styled.NotificationContainer
        transitionDuration={
          isOpen ? TRANSITION_DURATION : EXIT_TRANSITION_DURATION
        }
      >
        <Notification {...props} />
      </Styled.NotificationContainer>
    </CSSTransition>,
    containerRef.current
  );
};

NotificationContainer.propTypes = {
  /**
   * Determines whether notification is displayed or not. When closed, children are not mounted to the DOM.
   */
  isOpen: PropTypes.bool.isRequired
};

/**
 * A Notification is used for global messaging throughout the site.
 */
const Notification = ({
  variant,
  title,
  body,
  onRequestClose,
  buttons,
  hasStackedBtns,
  isFull,
  titleFontWeight,
  titleLink,
  className
}) => (
  <Styled.Notification variant={variant} isFull={isFull} className={className}>
    <Styled.NotificationContent>
      <Styled.Header>
        <Styled.Title $fontWeight={titleFontWeight}>
          {title}{' '}
          {titleLink && (
            <Styled.TitleLink as="a" href={titleLink.link}>
              {titleLink.text}
            </Styled.TitleLink>
          )}
        </Styled.Title>

        {onRequestClose && (
          <Styled.CloseButton
            onClick={onRequestClose}
            aria-label="close notification"
          />
        )}
      </Styled.Header>
      {body && <Styled.Body>{body}</Styled.Body>}
      <Styled.ButtonContainer
        hasStackedBtns={hasStackedBtns}
        hide={!buttons.length}
      >
        {buttons.map((button, index) => (
          <Styled.Button
            key={index}
            variant={index === 0 ? 'defaultDark' : 'link'}
            isFull={buttons.length === 1}
            onClick={button.onClick}
          >
            {button.text}
          </Styled.Button>
        ))}
      </Styled.ButtonContainer>
    </Styled.NotificationContent>
  </Styled.Notification>
);

Notification.propTypes = {
  /**
   * The type of Notification to display
   */
  variant: PropTypes.oneOf(['info', 'success', 'error']),
  /**
   * Array of buttons to display, min of 0, max of 2
   */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })
  ),
  /**
   * Title text for the notification
   */
  title: PropTypes.string.isRequired,
  /**
   * Body text for the notification
   */
  body: PropTypes.string,
  /**
   * callback func to run when the Notification is closes
   */
  onRequestClose: PropTypes.func,
  /**
   * `true` formats two buttons in a column
   */
  hasStackedBtns: PropTypes.bool,
  /**
   * If `true`, button stretches across entire width of container.
   */
  isFull: PropTypes.bool,
  /**
   * sets the font weight of the title text and not the title link (if there is one)
   */
  titleFontWeight: PropTypes.oneOf([400, 500, 700, 800]),
  /**
   * If exists, render an anchor tag positioned after the title
   */
  titleLink: PropTypes.shape({
    link: PropTypes.string,
    text: PropTypes.string
  }),
  /**
   * class name for styled components
   */
  className: PropTypes.string
};

Notification.defaultProps = {
  variant: 'info',
  buttons: [],
  body: undefined,
  hasStackedBtns: false,
  isFull: false,
  onRequestClose: undefined,
  titleFontWeight: undefined,
  titleLink: undefined,
  className: null
};

export { Notification as NotificationBody };
export default NotificationContainer;
